import { AuthActions, getNetworkErrorMessage } from '@framework/auth';
import { useTranslation } from '@framework/i18n';
import {
  LinkButton,
  LoaderSpin,
  popSessionStorageLocation,
  useHistory,
} from '@framework/ui';
import { Result, message } from 'antd';
import qs from 'qs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePostOpenwebJwtLogin } from '../../api/login-openweb-controller/login-openweb-controller';

export interface IOpenwebLoginPageProps {}

/**
 * Pagina di caricamento post-redirect di openweb che compie la callback
 * automatica di login dopo il redirect.
 */
export function OpenwebLoginPage(props: IOpenwebLoginPageProps) {
  const history = useHistory();
  const dispatch = useDispatch();
  const login = usePostOpenwebJwtLogin();
  const { t } = useTranslation();

  // Estraggo l'eventuale errore dalla query string
  const { error: errorInUrl } = qs.parse(history.location.search, {
    ignoreQueryPrefix: true,
  });

  const isFetching = useRef(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    if (errorInUrl) return;

    const loginFn = async () => {
      // Check per evitare di chiamarla più volte
      if (isFetching.current) return;
      isFetching.current = true;

      try {
        const result = await login.mutateAsync();
        if (!result.accessToken || !result.refreshToken) {
          console.error('Errore durante il login', result); // prettier-ignore
          message.error('Si è verificato un errore durante il login, nessun access token fornito.'); // prettier-ignore
          return;
        }

        dispatch(
          AuthActions.loginSucceed({
            strategy: 'jwt',
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
          }),
        );
        history.push((popSessionStorageLocation() ?? '/') as any);
      } catch (e) {
        console.error(e);
        message.error(getNetworkErrorMessage(e, 'Errore durante il login'));
        setError(e);
      }
    };

    // Richiamo la funzione
    loginFn();
  }, [dispatch, history, login, errorInUrl]);

  const errorMessage = useMemo(
    () =>
      errorInUrl
        ? String(errorInUrl)
        : getNetworkErrorMessage(
            error,
            "Errore sconosciuto, contattare l'assistenza o riprovare più tardi",
          ),
    [error, errorInUrl],
  );

  if (error || errorInUrl) {
    return (
      <Result
        status="error"
        title={t(
          'user.auto-login.response.error.title',
          `Errore durante il login`,
        )}
        subTitle={`${t(
          'user.auto-login.response.error.sub-title',
          `Non è stato possibile completare la procedura di login.`,
        )} ${errorMessage}`}
        extra={
          <LinkButton
            type="primary"
            to={'/login' as any}
            children={t(
              'user.auto-login.response.success.submit-button',
              'Torna al Login',
            )}
          />
        }
      />
    );
  }

  return <LoaderSpin full fullscreen hasFullWidthChildren />;
}
